import { MetricFetchOutcome } from 'api/customerInsights';

import { CardDataState } from '../types';

// The bridge from a transport outcome to the screen's own data-state model, so the HTTP taxonomy
// does not become a second vocabulary the cards have to learn. `MetricFetchOutcome` says what
// HAPPENED to one request; `CardDataState` says what the reader is TOLD. They are deliberately not
// the same list — the transport keeps distinctions the presentation must collapse, and collapsing
// them here rather than at the call site is what keeps that decision in one place.
// PLA-8815 — contract PLA-8817, plan `metabase-data-seam.md` § V.4.

// 403 collapses FIVE causes into one identical body on purpose — metric absent from the catalogue,
// `intelligence_suite` off, metric outside the tenant's industry, a missing permission-chain link,
// or an unrecognised Host. We cannot tell which, so we must not imply one.
//
// Notably NOT `permissionRestricted`: that state asserts a specific cause, and a 403 here is only
// sometimes about permission. Claiming it would put "you do not have access" in front of a reader
// whose tenant simply has the feature switched off. On this grid a genuinely unpermitted card is
// omitted from the layout entirely, client-side, before any request is made
// (`permissions/useCardVisibility`), so a 403 reaching this function is already the other cases.
const NOT_AVAILABLE: CardDataState = { kind: 'notConfigured' };

// 404 `40415` is the backend saying this metric's question has not landed for this deployment, which
// on UAT is the documented steady state of five Customer 360 identifiers (perx-api
// `docs/domains/analytics.md` § D-49). It shared `notConfigured`'s words until PLA-8828: an awaited
// reading and a mis-set-up one send the reader to different people.
const AWAITING_DATA: CardDataState = { kind: 'awaitingData' };

const TECHNICAL_ERROR: CardDataState = { kind: 'technicalError' };

/**
 * The state a card shows when a metric's request did not yield rows.
 *
 * `undefined` for the `rows` outcome, because rows do not decide a card's state on their own: a card
 * is `ok` when ANY of its metrics calculated, and the per-metric `calculation_status` decides the
 * rest (plan § II.7). That is per-card assembly's job, and returning `undefined` here rather than a
 * guess is what stops this function quietly becoming it.
 */
export const cardStateFromOutcome = (outcome: MetricFetchOutcome): CardDataState | undefined => {
  switch (outcome.kind) {
    case 'rows':
      return undefined;
    case 'forbidden':
      return NOT_AVAILABLE;
    case 'notDeployed':
      return AWAITING_DATA;
    // 502 (Metabase failed, or answered with a body whose `status` was not `completed`), 504
    // (timeout), and anything unrecognised. A misspelled query parameter also lands here, since
    // perx-api forwards it unvalidated and Metabase rejects it — there is no 400 on this endpoint,
    // so a typo and an outage are the same event to a reader.
    default:
      return TECHNICAL_ERROR;
  }
};

export default cardStateFromOutcome;
